import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { SchedulingChartConfig } from '@pages/Dashboard/SchedulingPerformanceDetail/schedulingPerformanceDetail.mockData'
import {
  getAROver90,
  getAROver90Overview,
} from '@services/api/dashboard/finance.api'
import type { AROver90Result } from '@services/api/dashboard/finance.api'
import {
  computeChartAxis,
  detectPeriodSummaryThousandsScale,
  filterYtdPeriodItems,
  formatCurrency,
  formatGoalCurrencyLabel,
  formatHygieneVisitDate,
} from '@utils/dashboardFinance.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'
import { useClinicGoal } from './useClinicGoal'

export type UseAROver90Options = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

export type AROver90DayDetailRow = {
  id: string
  patientId: string
  patientName: string
  arType: 'Patient' | 'Insurance'
  balance90Plus: string
  daysPast90: string
  lastPaymentDate: string
}

export type UseAROver90Result = {
  loading: boolean
  error: string | null
  /** Response has arrived (`data != null`); independent of nonzero content. */
  hasPayload: boolean
  hasData: boolean
  summary: AROver90Result['summary'] | undefined
  balanceChartConfig: SchedulingChartConfig
  pctChartConfig: SchedulingChartConfig
  tableRows: AROver90DayDetailRow[]
  arOver90BalanceGoalLabel: string
}

const EMPTY_CHART_CONFIG: SchedulingChartConfig = {
  data: [],
  yDomain: [0, 1],
  yTicks: [0, 0.25, 0.5, 0.75, 1],
}

function buildPeriodChartConfig(
  buckets: { label: string; value: number }[],
  options?: {
    valueInThousands?: boolean
    referenceTotalDollars?: number
  },
): SchedulingChartConfig {
  if (!buckets.length) {
    return EMPTY_CHART_CONFIG
  }

  const rawValues = buckets.map((b) => b.value)
  const thousandsScale =
    options?.valueInThousands === true
      ? detectPeriodSummaryThousandsScale(
          rawValues,
          options.referenceTotalDollars,
        )
      : null

  const data = buckets.map((b, i) => ({
    label: b.label,
    value: options?.valueInThousands
      ? thousandsScale === 'thousands'
        ? b.value
        : b.value / 1000
      : b.value,
    isCurrent: i === buckets.length - 1,
  }))
  const axis = computeChartAxis(data.map((d) => d.value))

  return {
    data,
    yDomain: axis.yDomain,
    yTicks: axis.yTicks,
  }
}

function useAROver90FromFetcher({
  range,
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseAROver90Options & {
  fetcher: DashboardPeriodFetcher<Awaited<ReturnType<typeof getAROver90>>>
}): UseAROver90Result {
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    queryParams,
    fetcher,
    { enabled, queued },
  )
  const { goal: clinicGoal } = useClinicGoal({ range, date, enabled, queued })
  const arOver90BalanceGoalLabel = formatGoalCurrencyLabel(
    clinicGoal?.ar_over_90_balance_goal,
  )

  const balanceChartConfig = useMemo(() => {
    const buckets = filterYtdPeriodItems(
      range,
      date,
      data?.arOver90PeriodSummary ?? [],
      (b) => b.label,
    )
    return buildPeriodChartConfig(buckets, {
      valueInThousands: true,
      referenceTotalDollars: data?.summary?.arOver90?.current,
    })
  }, [data, range, date])

  const pctChartConfig = useMemo(() => {
    const buckets = filterYtdPeriodItems(
      range,
      date,
      data?.arOver90PercentPeriodSummary ?? [],
      (b) => b.label,
    )
    return buildPeriodChartConfig(buckets)
  }, [data, range, date])

  const tableRows: AROver90DayDetailRow[] = useMemo(
    () =>
      (data?.appointments ?? []).map((row, index) => ({
        id: String(index + 1),
        patientId: row.patientId,
        patientName: row.patientName,
        arType: row.arType === 'insurance' ? 'Insurance' : 'Patient',
        balance90Plus: formatCurrency(row.arOver90Balance, { compact: true }),
        daysPast90: String(row.daysPast90),
        lastPaymentDate: row.lastPaymentDate
          ? formatHygieneVisitDate(row.lastPaymentDate)
          : '—',
      })),
    [data],
  )

  return {
    loading,
    error,
    hasPayload: data != null,
    hasData: hasNonZeroDashboardValues(
      data?.summary.arOver90.current,
      data?.summary.arOver90Percent.current,
      data?.summary.arOver90AppointmentCount.current,
    ),
    summary: data?.summary,
    balanceChartConfig,
    pctChartConfig,
    tableRows,
    arOver90BalanceGoalLabel,
  }
}

export function useAROver90(options: UseAROver90Options): UseAROver90Result {
  return useAROver90FromFetcher({ ...options, fetcher: getAROver90 })
}

export function useAROver90Overview(
  options: UseAROver90Options,
): UseAROver90Result {
  return useAROver90FromFetcher({ ...options, fetcher: getAROver90Overview })
}
